import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, DollarSign, Receipt, Wallet, UserPlus, CalendarPlus, FileText, Calculator, Repeat } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api, errMsg } from "@/lib/api";
import { money, fmtTime, fmtDateTime, fmtDay, SERVICE_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Stat, StatusBadge, EmptyState, Loading } from "@/components/common";

const QUICK = [
  { to: "/clients?new=1", label: "Add client", icon: UserPlus, id: "quick-add-client" },
  { to: "/schedule?new=1", label: "Schedule job", icon: CalendarPlus, id: "quick-schedule-job" },
  { to: "/estimates/new", label: "New estimate", icon: FileText, id: "quick-new-estimate" },
  { to: "/invoices/new", label: "New invoice", icon: Receipt, id: "quick-new-invoice" },
  { to: "/calculator", label: "Quote price", icon: Calculator, id: "quick-calculator" },
];

function JobRow({ a, tz, testId }) {
  return (
    <Link to={`/jobs/${a.id}`} data-testid={testId} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
      <div className="w-16 shrink-0 font-display font-semibold text-slate-900 text-sm">{fmtTime(a.start, tz)}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">{a.client_name}</div>
        <div className="text-xs text-slate-500 truncate">{SERVICE_LABELS[a.service_type]} · {a.address || "No address"}</div>
        {a.checklist && <div className="text-xs text-teal-700 mt-0.5">Checklist {a.checklist.percent}%</div>}
      </div>
      <StatusBadge status={a.status} />
    </Link>
  );
}

function Trend({ data }) {
  return (
    <div className="h-64" data-testid="dashboard-trend-chart">
      <ResponsiveContainer width="100%" height={256} initialDimension={{ width: 600, height: 256 }}>
        <BarChart data={data} margin={{ left: -10, right: 8 }}>
          <CartesianGrid vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="month" tickFormatter={(m) => new Date(`${m}-01T00:00`).toLocaleString("en-US", { month: "short" })} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v) => money(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="invoiced" name="Invoiced" fill="#0F172A" radius={[4, 4, 0, 0]} />
          <Bar dataKey="collected" name="Cash collected" fill="#0D9488" radius={[4, 4, 0, 0]} />
          <Bar dataKey="expenses" name="Expenses" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Dashboard() {
  const { user, tz, isManager } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, []);

  if (error) return <p className="text-red-700" data-testid="dashboard-error">{error}</p>;
  if (!d) return <Loading label="Loading dashboard" />;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Hi, ${user.name.split(" ")[0]}`} subtitle={`${fmtDay(d.today)} · ${d.todays_appointments.length} job${d.todays_appointments.length === 1 ? "" : "s"} today`} />

      {isManager && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="quick-actions">
          {QUICK.map((q) => (
            <Link key={q.id} to={q.to} data-testid={q.id} className="shrink-0 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 min-h-[44px] text-sm font-medium text-slate-800 hover:border-teal-600 hover:text-teal-700 transition-colors">
              <q.icon className="h-4 w-4" /> {q.label}
            </Link>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Stat label="Today's jobs" value={d.todays_appointments.length} icon={CalendarDays} testId="stat-today-jobs" />
        <Stat label="Upcoming (7 days)" value={d.upcoming_count} icon={Clock} tone="navy" testId="stat-upcoming" />
        <Stat label="Completed this month" value={d.completed_this_month} icon={CheckCircle2} tone="navy" testId="stat-completed" />
        {isManager && <Stat label="Outstanding" value={money(d.outstanding_total)} sub={`${d.outstanding_count} invoices · ${d.overdue_count} overdue`} icon={Receipt} tone={d.overdue_count ? "red" : "amber"} testId="stat-outstanding" />}
        {isManager && <Stat label="Invoiced this month" value={money(d.invoiced_this_month)} sub="Revenue billed (sent invoices)" icon={DollarSign} testId="stat-invoiced" />}
        {isManager && <Stat label="Cash collected" value={money(d.collected_this_month)} sub="Payments received this month" icon={Wallet} testId="stat-collected" />}
        {isManager && <Stat label="Expenses this month" value={money(d.expenses_this_month)} icon={Wallet} tone="navy" testId="stat-expenses" />}
        {isManager && <Stat label="Est. operating profit" value={money(d.collected_this_month - d.expenses_this_month)} sub="Cash collected − expenses" icon={DollarSign} tone="navy" testId="stat-profit" />}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <Panel title="Today's schedule" className="lg:col-span-2" testId="today-panel" action={<Link to="/schedule" className="text-sm text-teal-700 hover:underline" data-testid="view-schedule-link">Open calendar</Link>}>
          {d.todays_appointments.length === 0
            ? <EmptyState icon={CalendarDays} title="No jobs today" text="Enjoy the breather, or schedule a cleaning." testId="today-empty" />
            : d.todays_appointments.map((a) => <JobRow key={a.id} a={a} tz={tz} testId={`today-job-${a.id}`} />)}
        </Panel>

        {isManager ? (
          <Panel title="Outstanding invoices" testId="outstanding-panel" action={<Link to="/invoices" className="text-sm text-teal-700 hover:underline">All</Link>}>
            {d.outstanding_invoices.length === 0 ? <EmptyState icon={Receipt} title="All caught up" text="No unpaid invoices." testId="outstanding-empty" /> : d.outstanding_invoices.map((i) => (
              <Link key={i.id} to={`/invoices/${i.id}`} data-testid={`outstanding-${i.id}`} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                <div className="min-w-0"><div className="font-medium text-slate-900 truncate">{i.client_name}</div><div className="text-xs text-slate-500">{i.number} · due {fmtDay(i.due_date)}</div></div>
                <div className="text-right"><div className="font-semibold text-slate-900">{money(i.balance)}</div><StatusBadge status={i.payment_status} /></div>
              </Link>
            ))}
          </Panel>
        ) : (
          <Panel title="Upcoming jobs" testId="upcoming-panel">
            {d.upcoming.length === 0 ? <EmptyState title="Nothing scheduled" /> : d.upcoming.map((a) => (
              <Link key={a.id} to={`/jobs/${a.id}`} className="block py-3 border-b border-slate-100 last:border-0"><div className="font-medium">{a.client_name}</div><div className="text-xs text-slate-500">{fmtDateTime(a.start, tz)}</div></Link>
            ))}
          </Panel>
        )}

        <Panel title="Upcoming recurring" testId="recurring-panel">
          {d.upcoming_recurring.length === 0 ? <EmptyState icon={Repeat} title="No recurring cleans yet" text="Set a weekly or biweekly schedule when booking a job." testId="recurring-empty" /> : d.upcoming_recurring.map((a) => (
            <Link key={a.id} to={`/jobs/${a.id}`} data-testid={`recurring-${a.id}`} className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
              <Repeat className="h-4 w-4 text-teal-700 shrink-0" />
              <div className="min-w-0"><div className="font-medium text-slate-900 truncate">{a.client_name}</div><div className="text-xs text-slate-500">{fmtDateTime(a.start, tz)}</div></div>
            </Link>
          ))}
        </Panel>

        {isManager && (
          <Panel title="Last 6 months" className="lg:col-span-2" testId="trend-panel">
            <Trend data={d.trend} />
          </Panel>
        )}
      </div>
    </div>
  );
}
